/**
 * /creatives as one CSV (spec §7, R15): every row after the filters and the sort, never just the first 15, with the
 * page's columns plus the ad ID, channel, format, campaign, status, labels and age. Provenance lines come first:
 * title, scope, period, Result window, data date, filters, sort, currency, the creatives left out, a failed
 * definition or D1's Reach line, the Awareness unit, the sync, and when the file was made.
 *
 * A NULL is written as the page shows it, as D1's and D2's files write it (rules-04 D4): "—", or "ไม่ได้รายงาน" for an
 * Engagement the platform did not send. A missing ad ID stays an empty cell (rules-04 D review Q3); every other NULL
 * is "—" or that word.
 * Money is raw with its currency in the header, and ratios are percent numbers (D1's num / pct / unit).
 * Words come from creativeText, so the file says what the page says. Pure, except `downloadCreativeCsv`.
 *
 * @module services/creatives/creativeCsv
 */
import type { ChannelFreshnessState } from '@/hooks/useLookerChannelFreshness';
import { CHANNEL_LABEL, campaignStateText, type TFn } from '@/services/channels/channelFormat';
import { downloadCsv, type CsvCell } from '@/services/csv';
import { syncLine, utcMinute, type DashboardCsvLabels } from '@/services/looker/lookerCsv';
import { num, pct, unit } from '@/services/overview/boxTableParts';
import { DASH } from '@/services/overview/overviewFormat';
import type { ResultMappingInfo } from '@/services/targets/resultMetric';
import { creativeFormatLabel } from './creativeFormat';
import {
  ageText, channelFilterText, creativeNameOf, emptyTextOf, formatFilterLabel, labelTextsOf, objectiveFilterText,
  resultCellOf, ruleNotesOf, sortLabel,
} from './creativeText';
import type { CreativeFilters, CreativeRow, CreativeView } from './creativeTypes';
import type { CreativeWindows } from './creativeWindows';

export interface CreativeCsvInput {
  allWorkspaces: boolean;
  /** The workspace's name; null while it loads. */
  workspaceName: string | null;
  windows: CreativeWindows;
  view: CreativeView;
  filters: CreativeFilters;
  /** null while it loads; `failed` blanks Awareness (R6). */
  mapping: ResultMappingInfo | null;
  freshness: ChannelFreshnessState;
  /** creativeDataDateOf(freshness, view). */
  dataDate: string | null;
}

/** R15: a NULL is written as the page shows it. */
const orDash = (c: CsvCell): CsvCell => (c === null ? DASH : c);

/** The Result as the file writes it: the raw figure, or the page's word ("ไม่ได้รายงาน", "—"). */
const resultCell = (r: CreativeRow, t: TFn): CsvCell => {
  if (r.metric === null || r.resultState === 'none') return DASH;
  return r.resultState === 'notReported' ? t('overview.notReported') : orDash(num(r.result));
};

const columnsOf = (t: TFn, currency: string | null): string[] => [
  t('creatives.col.creative'), t('creatives.csv.externalId'), t('creatives.csv.channel'), t('creatives.format.label'),
  t('creatives.csv.campaign'), t('creatives.csv.status'), t('creatives.csv.labels'), t('creatives.col.age'),
  unit(t('creatives.col.spend'), currency), t('creatives.col.impressions'), t('creatives.col.clicks'),
  unit(t('creatives.col.ctr'), '%'), unit(t('creatives.col.cpc'), currency), unit(t('creatives.col.cpm'), currency),
  t('creatives.col.result'), t('creatives.csv.resultMetric'), unit(t('creatives.col.costPerResult'), currency),
  t('creatives.col.engagement'), unit(t('creatives.col.viewRate'), '%'),
];

const rowCells = (r: CreativeRow, t: TFn): CsvCell[] => [
  // A missing ad ID is an absent dimension: an empty cell (rules-04 D review Q3).
  creativeNameOf(r, t), r.externalAdId, CHANNEL_LABEL[r.platform], creativeFormatLabel(r.format, t),
  r.campaignName, campaignStateText(r.state, t), labelTextsOf(r, t).join('; '), ageText(r.age, t),
  orDash(num(r.spend)), orDash(num(r.impressions)), orDash(num(r.clicks)),
  orDash(pct(r.ctr)), orDash(num(r.cpc)), orDash(num(r.cpm)),
  resultCell(r, t), resultCellOf(r, t).metric ?? DASH, orDash(num(r.costPerResult)),
  r.engagement === null ? t('overview.notReported') : num(r.engagement), orDash(pct(r.viewRate)),
];

const scopeOf = (input: CreativeCsvInput, l: DashboardCsvLabels): string =>
  (input.allWorkspaces ? l.allWorkspaces : input.workspaceName ?? '');

/** The definition as D1's file says it: a failed read, or Reach with its caveat (rules-04 C2); else no line. */
const definitionLine = (mapping: ResultMappingInfo | null, l: DashboardCsvLabels, t: TFn): string | null => {
  if (mapping?.failed) return t('creatives.notes.mappingFailed');
  return mapping?.awareness === 'reach' ? l.resultMappingReach : null;
};

/** Box 2: its title, then the table (every row), the page's empty line, or the state a table that is not ready is in. */
export const creativeCsvRows = (input: CreativeCsvInput, l: DashboardCsvLabels, t: TFn): CsvCell[][] => {
  const title = [t('creatives.box.table')];
  const table = input.view.table;
  if (table.status === 'failed') return [title, [l.appFailed]];
  if (table.status !== 'ready') return [title, [l.loading]];
  const notes = ruleNotesOf(t).map((n) => [n]);
  if (table.data.rows.length === 0) return [title, [emptyTextOf(table.data, scopeOf(input, l), input.filters, t)], ...notes];
  const currency = input.view.money?.currency ?? null;
  return [title, columnsOf(t, currency), ...table.data.rows.map((r) => rowCells(r, t)), ...notes];
};

/** R15's order; ISO dates, and the file's own time in UTC (a file has no viewer clock). */
export const creativeProvenance = (input: CreativeCsvInput, l: DashboardCsvLabels, t: TFn, now: Date): string[] => {
  const { windows: w, view, filters: f } = input;
  return [
    t('creatives.title'),
    scopeOf(input, l),
    t('creatives.csv.periodLine', { start: w.start, end: w.end }),
    w.resultEnd === null ? t('creatives.notes.noResultWindow') : t('creatives.csv.resultLine', { date: w.resultEnd }),
    input.dataDate ? l.dataAsOf(input.dataDate) : null,
    t('creatives.csv.filtersLine', {
      channels: channelFilterText(f.channels, t), objective: objectiveFilterText(f.objective, t), format: formatFilterLabel(f.format, t),
    }),
    t('creatives.csv.sortLine', { sort: sortLabel(f.sort, t) }),
    view.money?.currency ? l.currency(view.money.currency) : null,
    view.unknownCurrency > 0 ? t('creatives.notes.unknownCurrency', { n: view.unknownCurrency }) : null,
    definitionLine(input.mapping, l, t),
    t('creatives.csv.awarenessPerThousand'),
    syncLine(input.freshness, l),
    t('creatives.csv.madeAt', { at: utcMinute(now.toISOString()) }),
  ].filter((line): line is string => Boolean(line));
};

/**
 * R16: a file needs the table drawn and the sync line read. While the facts or the Result definition load, or after the
 * facts failed, there is nothing to write (spec §7); a file written while the freshness read runs would miss its line.
 */
export const creativeCsvReady = (view: Pick<CreativeView, 'table'>, freshness: ChannelFreshnessState): boolean =>
  view.table.status === 'ready' && freshness.status !== 'loading';

export const creativeCsvFilename = (w: Pick<CreativeWindows, 'start' | 'end'>): string => `creative-performance_${w.start}_${w.end}.csv`;

export const downloadCreativeCsv = (input: CreativeCsvInput, l: DashboardCsvLabels, t: TFn, now: Date = new Date()): void => {
  downloadCsv(creativeCsvFilename(input.windows), creativeCsvRows(input, l, t), { provenance: creativeProvenance(input, l, t, now) });
};
